import React, { useState } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Spinner,
  Select,
  SelectItem,
} from '@heroui/react'

import { useToast } from '../../../hooks'
import {
  analyzeGifUrlFormats,
  repairGifUrlFormats,
  type GifUrlAnalysisResult,
  type GifUrlRepairResult,
} from '../services/mediaManagementService'

// Simple icon components
const AnalysisIcon = () => <span>🔍</span>

export const UrlAnalysis: React.FC = () => {
  // URL Analysis state
  const [urlAnalysisData, setUrlAnalysisData] =
    useState<GifUrlAnalysisResult | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)

  // URL Repair state
  const [urlRepairData, setUrlRepairData] = useState<GifUrlRepairResult | null>(
    null
  )
  const [isRepairing, setIsRepairing] = useState(false)
  const [repairSettings, setRepairSettings] = useState({
    dryRun: true,
    maxRepairs: 100,
  })

  const toast = useToast()

  // URL Analysis Functions
  const runUrlAnalysis = async () => {
    setIsAnalyzing(true)
    try {
      const result = await analyzeGifUrlFormats()
      setUrlAnalysisData(result)
      toast.success('URL format analysis completed successfully!')
    } catch (error) {
      console.error('Error analyzing URL formats:', error)
      toast.error(
        `Failed to analyze URL formats: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    } finally {
      setIsAnalyzing(false)
    }
  }

  const runUrlRepair = async () => {
    setIsRepairing(true)
    try {
      const result = await repairGifUrlFormats({
        dryRun: repairSettings.dryRun,
        maxRepairs: repairSettings.maxRepairs,
      })
      setUrlRepairData(result)

      if (result.dryRun) {
        toast.success(
          `Dry run completed! Found ${result.repairsNeeded} URLs that need repair.`
        )
      } else {
        toast.success(
          `Repair completed! Fixed ${result.repairsCompleted} URLs successfully.`
        )
        // Refresh analysis data after actual repair
        if (result.repairsCompleted > 0) {
          setTimeout(() => runUrlAnalysis(), 1000)
        }
      }
    } catch (error) {
      console.error('Error repairing URL formats:', error)
      toast.error(
        `Failed to repair URL formats: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    } finally {
      setIsRepairing(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* URL Analysis Overview Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <div>
              <h3 className="text-lg font-semibold">GIF URL Format Analysis</h3>
              <p className="text-slate-600 dark:text-slate-400">
                Analyze all GIF URLs in the database to identify format
                inconsistencies
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onPress={runUrlAnalysis}
                isDisabled={isAnalyzing}
                color="primary"
                variant="flat"
                startContent={
                  isAnalyzing ? <Spinner size="sm" /> : <AnalysisIcon />
                }
              >
                {isAnalyzing ? 'Analyzing...' : 'Analyze URL Formats'}
              </Button>
            </div>
          </div>
        </CardHeader>
        {urlAnalysisData && (
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {urlAnalysisData.urlFormats.storagePathFormat.count}
                </div>
                <div className="text-sm text-green-600 dark:text-green-400">
                  Correct Format
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {urlAnalysisData.urlFormats.storagePathFormat.pattern}
                </div>
              </div>
              <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {urlAnalysisData.urlFormats.fullUrlFormat.count}
                </div>
                <div className="text-sm text-orange-600 dark:text-orange-400">
                  Full URLs
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {urlAnalysisData.urlFormats.fullUrlFormat.pattern}
                </div>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {urlAnalysisData.urlFormats.otherFormats.count}
                </div>
                <div className="text-sm text-red-600 dark:text-red-400">
                  Other Formats
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Unexpected formats
                </div>
              </div>
            </div>

            {/* Recommendations */}
            {urlAnalysisData.recommendations.length > 0 && (
              <div className="mb-6">
                <h4 className="text-md font-semibold mb-3">Recommendations</h4>
                <div className="space-y-2">
                  {urlAnalysisData.recommendations.map(
                    (recommendation, index) => (
                      <div
                        key={index}
                        className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-400"
                      >
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          {recommendation}
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Examples */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Correct Format Examples */}
              {urlAnalysisData.urlFormats.storagePathFormat.examples.length >
                0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-green-600 dark:text-green-400">
                    ✅ Correct Format Examples
                  </h4>
                  <div className="space-y-1">
                    {urlAnalysisData.urlFormats.storagePathFormat.examples.map(
                      (example, index) => (
                        <div
                          key={index}
                          className="text-xs font-mono bg-green-50 dark:bg-green-900/20 p-2 rounded"
                        >
                          {example}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Full URL Examples */}
              {urlAnalysisData.urlFormats.fullUrlFormat.examples.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-orange-600 dark:text-orange-400">
                    ⚠️ Full URL Examples
                  </h4>
                  <div className="space-y-1">
                    {urlAnalysisData.urlFormats.fullUrlFormat.examples.map(
                      (example, index) => (
                        <div
                          key={index}
                          className="text-xs font-mono bg-orange-50 dark:bg-orange-900/20 p-2 rounded break-all"
                        >
                          {example}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Other Format Examples */}
            {urlAnalysisData.urlFormats.otherFormats.examples.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold mb-2 text-red-600 dark:text-red-400">
                  ❌ Other Format Examples
                </h4>
                <div className="space-y-1">
                  {urlAnalysisData.urlFormats.otherFormats.examples.map(
                    (example, index) => (
                      <div
                        key={index}
                        className="text-xs bg-red-50 dark:bg-red-900/20 p-2 rounded"
                      >
                        <div className="font-mono break-all">{example.url}</div>
                        <div className="text-slate-500 mt-1">
                          GIF ID: {example.gifId}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </CardBody>
        )}
      </Card>

      {/* URL Repair Card */}
      {urlAnalysisData &&
        urlAnalysisData.urlFormats.fullUrlFormat.count > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between w-full">
                <div>
                  <h3 className="text-lg font-semibold">URL Repair</h3>
                  <p className="text-slate-600 dark:text-slate-400">
                    Convert full URLs to storage path format to fix metadata
                    lookup
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                {/* Repair Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Select
                      label="Mode"
                      selectedKeys={[
                        repairSettings.dryRun ? 'dry-run' : 'execute',
                      ]}
                      onSelectionChange={(keys) => {
                        const key = Array.from(keys)[0] as string
                        setRepairSettings((prev) => ({
                          ...prev,
                          dryRun: key === 'dry-run',
                        }))
                      }}
                      className="w-full"
                    >
                      <SelectItem key="dry-run">
                        Dry Run (Preview Only)
                      </SelectItem>
                      <SelectItem key="execute">Execute Repairs</SelectItem>
                    </Select>
                  </div>
                  <div>
                    <Input
                      type="number"
                      label="Max Repairs"
                      value={repairSettings.maxRepairs.toString()}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 100
                        setRepairSettings((prev) => ({
                          ...prev,
                          maxRepairs: Math.min(Math.max(value, 1), 1000),
                        }))
                      }}
                      min={1}
                      max={1000}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Repair Button */}
                <div className="flex items-center gap-4">
                  <Button
                    onPress={runUrlRepair}
                    isDisabled={isRepairing}
                    color={repairSettings.dryRun ? 'primary' : 'warning'}
                    variant="flat"
                    startContent={
                      isRepairing ? <Spinner size="sm" /> : <span>🔧</span>
                    }
                  >
                    {isRepairing
                      ? 'Processing...'
                      : repairSettings.dryRun
                        ? 'Preview Repairs'
                        : 'Execute Repairs'}
                  </Button>
                  {!repairSettings.dryRun && (
                    <div className="text-sm text-orange-600 dark:text-orange-400">
                      ⚠️ This will modify your database
                    </div>
                  )}
                </div>

                {/* Repair Results */}
                {urlRepairData && (
                  <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <h4 className="text-md font-semibold mb-3">
                      {urlRepairData.dryRun
                        ? 'Repair Preview'
                        : 'Repair Results'}
                    </h4>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="text-center">
                        <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                          {urlRepairData.totalProcessed}
                        </div>
                        <div className="text-xs text-slate-500">
                          Total Processed
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-orange-600 dark:text-orange-400">
                          {urlRepairData.repairsNeeded}
                        </div>
                        <div className="text-xs text-slate-500">
                          Need Repair
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600 dark:text-green-400">
                          {urlRepairData.repairsCompleted}
                        </div>
                        <div className="text-xs text-slate-500">
                          {urlRepairData.dryRun ? 'Would Fix' : 'Fixed'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-red-600 dark:text-red-400">
                          {urlRepairData.repairsFailed}
                        </div>
                        <div className="text-xs text-slate-500">Failed</div>
                      </div>
                    </div>

                    {/* Before/After Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h5 className="text-sm font-semibold mb-2">
                          Before Repair
                        </h5>
                        <div className="space-y-1 text-xs">
                          <div>
                            ✅ Correct:{' '}
                            {
                              urlRepairData.summary.beforeRepair
                                .storagePathFormat
                            }
                          </div>
                          <div>
                            ⚠️ Full URLs:{' '}
                            {urlRepairData.summary.beforeRepair.fullUrlFormat}
                          </div>
                          <div>
                            ❌ Other:{' '}
                            {urlRepairData.summary.beforeRepair.otherFormats}
                          </div>
                        </div>
                      </div>
                      <div>
                        <h5 className="text-sm font-semibold mb-2">
                          {urlRepairData.dryRun
                            ? 'After Repair (Preview)'
                            : 'After Repair'}
                        </h5>
                        <div className="space-y-1 text-xs">
                          <div>
                            ✅ Correct:{' '}
                            {
                              urlRepairData.summary.afterRepair
                                .storagePathFormat
                            }
                          </div>
                          <div>
                            ⚠️ Full URLs:{' '}
                            {urlRepairData.summary.afterRepair.fullUrlFormat}
                          </div>
                          <div>
                            ❌ Other:{' '}
                            {urlRepairData.summary.afterRepair.otherFormats}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}
    </div>
  )
}

export default UrlAnalysis
